import { promiseTimeout } from "@vueuse/core";
import { onMounted, ref } from "vue";
import { createInstamedApi } from "#ce/components/SpPayment/api/instamed";
import { PaymentError, PaymentErrorCode } from "#ce/components/SpPayment/lib/error";
import { HttpStatus } from "#/shared/lib/http";
import { navigateTo } from "#/shared/lib/navigation";

const requiredScripts = {
  token: "https://cdn.instamed.com/Content/Js/token.js",
  secureTokenHelper: "https://developers.instamed.com/wp-content/themes/devportal/js/secureTokenHelper.js",
};

async function loadScript(url, data = {}) {
  if (hasScript(url)) {
    return;
  }

  const script = document.createElement("script");
  script.src = url;

  for (const [key, value] of Object.entries(data)) {
    script.dataset[key.toLowerCase()] = value;
  }

  // We append to the body because token.js will check for the last script in the document
  // and use that to determine the values of the data attributes
  document.body.appendChild(script);

  return new Promise((resolve, reject) => {
    script.onload = resolve;
    script.onerror = reject;
  });
}

function hasScript(url) {
  const script = document.querySelector(`script[src*='${url}']`);
  return script !== null;
}

async function loadInstamedScripts(config) {
  await loadScript(requiredScripts.secureTokenHelper);
  await loadScript(requiredScripts.token, config);
}

async function ensureInstamedScripts(config) {
  try {
    await loadInstamedScripts(config);
  } catch (err) {
    console.error("Error loading Instamed scripts", err);
    throw new PaymentError("Failed to load Instamed scripts", err);
  }
}

const CONNECT_TIMEOUT = 300;
const MAX_CONNECT_TIMEOUT = 1000;
const MAX_CONNECT_ATTEMPTS = 10;

const ERROR_NO_CARD_INFO = "No card info. Please add a card to continue.";

const DEFAULT_DISPLAY_MODE = "incontext";
const DEFAULT_MOBILE_DISPLAY_MODE = DEFAULT_DISPLAY_MODE;
const DEFAULT_POPUP_WIDTH = "400";
const DEFAULT_POPUP_HEIGHT = "600";

const ResponseStatus = {
  Cancelled: "Cancelled",
};

/**
 * The Instamed composable is a factory function that returns a Instamed composable based on the Instamed config.
 * It is used to create and mount the Instamed payment element.
 * It also provides a wrapper around the global window.addCard function.
 *
 * @param {string} host - The host of the payment element (not used)
 * @param {Object} config
 * @param {string} config.displayMode
 * @param {string} config.environment
 * @param {string} config.mobileDisplayMode
 * @param {string} config.publishableKey - The account ID for the Instamed account
 */
export function useInstamed(host, config) {
  const api = createInstamedApi();

  const paymentType = ref(null);
  const instance = ref(null);
  const cardInfo = ref(null);
  const isPaymentModalOpen = ref(false);

  const {
    publishableKey,
    environment,
    displayMode = DEFAULT_DISPLAY_MODE,
    mobileDisplayMode = DEFAULT_MOBILE_DISPLAY_MODE,
    popupWidth = DEFAULT_POPUP_WIDTH,
    popupHeight = DEFAULT_POPUP_HEIGHT,
    organizationName,
  } = config;

  const scriptConfig = {
    environment,
    displayMode,
    mobileDisplayMode,
  };

  async function connect(attempt = 0) {
    if (instance.value) {
      return;
    }

    if (attempt > MAX_CONNECT_ATTEMPTS) {
      throw new PaymentError("Failed to connect to Instamed");
    }

    await ensureInstamedScripts(scriptConfig);
    instance.value = window.InstaMed2;

    if (!instance.value) {
      // Increment the timeout for each attempt in order to avoid overwhelming the browser
      await promiseTimeout(CONNECT_TIMEOUT * Math.min(attempt + 1, MAX_CONNECT_TIMEOUT));
      await connect(attempt + 1);
    }
  }

  async function createAndMountElement() {
    if (!instance.value) {
      await connect();
    }

    handleInstanceCreated();
  }

  /**
   * Handles the creation of the Instamed instance.
   * It needs to prepare the environment for the Instamed iframe to be loaded.
   */
  function handleInstanceCreated() {
    // This is the global Instamed function that adds the JSONP script to the document.
    // It is used to load the Instamed iframe and support the internals of addCard.
    instance.value.addJsonP();
  }

  /**
   * Handles the response from the Instamed iframe
   * Instead of setting the card info on the global window object, we set it locally
   * and let the reactive system handle the updates.
   *
   * @param {Object} response - The response from the Instamed iframe
   * @override {window.handleResponse} - The global Instamed handleResponse function
   */
  function handleResponse(response) {
    isPaymentModalOpen.value = false;

    cardInfo.value = extractCardInfo(response);
    paymentType.value = cardInfo.value ? "card" : null;

    // This is in essence what the original handleResponse function did
    if (!window.InstaMed2.isMobile()) {
      document.getElementById("instamed").remove();
    }
  }

  /**
   * Extracts the card info from the response.
   *
   * @param {Object} response - The response from the Instamed iframe
   * @returns {Object|null} - The card info or null if the response is cancelled
   */
  function extractCardInfo(response) {
    if (response.Status == ResponseStatus.Cancelled) {
      return null;
    }

    return {
      cardNumber: response.Token,
      maskedCardNumber: instance.value.maskCardNumber(response.Token),
      expDate: response.ExpDate,
    };
  }

  /**
   * Wrapper around the global window.addCard function.
   * It simply calls the global function with the given Instamed props.
   *
   * We need to patch the global handleResponse function to handle the response from the Instamed iframe locally
   * because the global function is not reactive and relies on input elements to be present in the DOM.
   */
  function addCard() {
    // Add card will open the Instamed iframe in a new modal.
    // Let consumers of the payment element know that a new modal is going to be opened.
    isPaymentModalOpen.value = true;

    try {
      // We need to patch the global handleResponse function to handle the response from the Instamed iframe locally.
      // This has to be done ALWAYS before calling the global addCard function because we can not control the order of execution.
      window.handleResponse = handleResponse;
      window.addCard(publishableKey, popupWidth, popupHeight);
    } catch (err) {
      console.error("Error adding card", err);
      isPaymentModalOpen.value = false;
      throw new PaymentError("Failed to add card", err);
    }
  }

  function removeCard() {
    instance.value.removeCard();
    cardInfo.value = null;
  }

  function validatePayment() {
    const error =
      cardInfo.value === null ? new PaymentError(ERROR_NO_CARD_INFO, undefined, PaymentErrorCode.NO_CARD_INFO) : null;
    return { error };
  }

  async function confirmPayment({ captureUrl, returnUrl }) {
    const { error: validationError } = validatePayment();

    if (validationError) {
      return { error: validationError };
    }

    try {
      await api.capture(captureUrl, cardInfo.value);
      navigateTo(returnUrl);
      return { error: null };
    } catch (err) {
      console.error("Error capturing payment", err);
      // The server answers a declined capture with a 400 and the reason, and has already reported it.
      const declined = err.status === HttpStatus.BAD_REQUEST && Boolean(err.data?.error);
      const code = declined ? PaymentErrorCode.CAPTURE_DECLINED : undefined;
      return { error: new PaymentError(err.data?.error ?? err.message, undefined, code) };
    }
  }

  onMounted(async () => {
    await ensureInstamedScripts(scriptConfig);
  });

  return {
    // State
    paymentType,
    cardInfo,
    customInterface: "InstamedPaymentInterface",
    hasPaymentModal: true,
    isPaymentModalOpen,
    organizationName,

    // Methods
    createAndMountElement,
    addCard,
    removeCard,
    validatePayment,
    confirmPayment,
  };
}
